/**
 * BatchUploadForm Component
 *
 * Form for uploading CSV files to create batch jobs with LLM-powered parsing.
 */

import React, { useState } from 'react';

interface BatchUploadFormProps {
  projectId: string;
  onSubmit: (data: {
    file: File;
    name: string;
    description: string;
    parsing_description: string;
    parsing_concurrency: number;
    execution_concurrency: number;
  }) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const BatchUploadForm: React.FC<BatchUploadFormProps> = ({
  projectId,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parsing_description: '',
    parsing_concurrency: 50,
    execution_concurrency: 10,
  });

  const [file, setFile] = useState<File | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = (name: string, value: string | number | File | null) => {
    let error = '';

    switch (name) {
      case 'file':
        if (!value) {
          error = 'CSV file is required';
        } else if (value instanceof File) {
          if (!value.name.endsWith('.csv')) {
            error = 'File must be a CSV file';
          }
          if (value.size > 10 * 1024 * 1024) {
            error = 'File size must be less than 10MB';
          }
        }
        break;
      case 'name':
        if (!value || (typeof value === 'string' && !value.trim())) {
          error = 'Batch name is required';
        } else if (typeof value === 'string' && value.length > 255) {
          error = 'Batch name must be 255 characters or less';
        }
        break;
      case 'parsing_description':
        if (!value || (typeof value === 'string' && !value.trim())) {
          error = 'Parsing description is required';
        } else if (typeof value === 'string' && value.length > 5000) {
          error = 'Parsing description must be 5000 characters or less';
        }
        break;
      case 'parsing_concurrency':
        if (typeof value === 'number') {
          if (value < 1 || value > 100) {
            error = 'Parsing concurrency must be between 1 and 100';
          }
        }
        break;
      case 'execution_concurrency':
        if (typeof value === 'number') {
          if (value < 1 || value > 50) {
            error = 'Execution concurrency must be between 1 and 50';
          }
        }
        break;
    }

    return error;
  };

  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    let value: string | number | File | null;

    if (field === 'file') {
      value = file;
    } else {
      value = formData[field as keyof typeof formData];
    }

    const error = validateField(field, value);
    if (error) {
      setErrors((prev) => ({ ...prev, [field]: error }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    setTouched((prev) => ({ ...prev, file: true }));

    const error = validateField('file', selectedFile);
    if (error) {
      setErrors((prev) => ({ ...prev, file: error }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.file;
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const fieldsToValidate = ['file', 'name', 'parsing_description', 'parsing_concurrency', 'execution_concurrency'];
    const newTouched: Record<string, boolean> = {};
    const newErrors: Record<string, string> = {};

    fieldsToValidate.forEach((field) => {
      newTouched[field] = true;
      let value: string | number | File | null;

      if (field === 'file') {
        value = file;
      } else {
        value = formData[field as keyof typeof formData];
      }

      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    setTouched(newTouched);
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0 && file) {
      onSubmit({
        file,
        name: formData.name.trim(),
        description: formData.description.trim(),
        parsing_description: formData.parsing_description.trim(),
        parsing_concurrency: formData.parsing_concurrency,
        execution_concurrency: formData.execution_concurrency,
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Upload CSV for Batch Jobs
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
            {/* CSV File Upload */}
            <div>
              <label htmlFor="file" className="block text-sm font-medium text-gray-700 mb-1">
                CSV File <span className="text-red-500">*</span>
              </label>
              <input
                type="file"
                id="file"
                accept=".csv"
                onChange={handleFileChange}
                onBlur={() => handleBlur('file')}
                disabled={isLoading}
                className={`block w-full text-sm text-gray-900 border rounded-md cursor-pointer focus:outline-none ${
                  touched.file && errors.file
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                aria-invalid={touched.file && !!errors.file}
              />
              <p className="mt-1 text-xs text-gray-500">
                Upload a CSV file (max 10MB, up to 100,000 rows)
              </p>
              {touched.file && errors.file && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {errors.file}
                </p>
              )}
            </div>

            {/* Batch Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Batch Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                onBlur={() => handleBlur('name')}
                disabled={isLoading}
                className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
                  touched.name && errors.name
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="e.g., Evaluation Batch 2025-01-15"
                aria-invalid={touched.name && !!errors.name}
              />
              {touched.name && errors.name && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Description Field (Optional) */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-gray-500">(Optional)</span>
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                disabled={isLoading}
                rows={2}
                className="block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Brief description of this batch..."
              />
            </div>

            {/* Parsing Description Field */}
            <div>
              <label htmlFor="parsing_description" className="block text-sm font-medium text-gray-700 mb-1">
                Parsing Instructions <span className="text-red-500">*</span>
              </label>
              <textarea
                id="parsing_description"
                value={formData.parsing_description}
                onChange={(e) => setFormData({ ...formData, parsing_description: e.target.value })}
                onBlur={() => handleBlur('parsing_description')}
                disabled={isLoading}
                rows={4}
                className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
                  touched.parsing_description && errors.parsing_description
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                }`}
                placeholder="Example: Extract 'name' from column A, 'email' from column B, and 'website_url' from column C."
                aria-invalid={touched.parsing_description && !!errors.parsing_description}
              />
              <p className="mt-1 text-xs text-gray-500">
                Describe how to transform CSV columns into job inputs. The AI will generate Python code based on your instructions.
              </p>
              {touched.parsing_description && errors.parsing_description && (
                <p className="mt-1 text-sm text-red-600" role="alert">
                  {errors.parsing_description}
                </p>
              )}
            </div>

            {/* Concurrency Settings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Parsing Concurrency */}
              <div>
                <label htmlFor="parsing_concurrency" className="block text-sm font-medium text-gray-700 mb-1">
                  Parsing Concurrency
                </label>
                <input
                  type="number"
                  id="parsing_concurrency"
                  value={formData.parsing_concurrency}
                  onChange={(e) => setFormData({ ...formData, parsing_concurrency: parseInt(e.target.value) || 50 })}
                  onBlur={() => handleBlur('parsing_concurrency')}
                  disabled={isLoading}
                  min={1}
                  max={100}
                  className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
                    touched.parsing_concurrency && errors.parsing_concurrency
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  aria-invalid={touched.parsing_concurrency && !!errors.parsing_concurrency}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Default: 50 (1-100)
                </p>
                {touched.parsing_concurrency && errors.parsing_concurrency && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {errors.parsing_concurrency}
                  </p>
                )}
              </div>

              {/* Execution Concurrency */}
              <div>
                <label htmlFor="execution_concurrency" className="block text-sm font-medium text-gray-700 mb-1">
                  Execution Concurrency
                </label>
                <input
                  type="number"
                  id="execution_concurrency"
                  value={formData.execution_concurrency}
                  onChange={(e) => setFormData({ ...formData, execution_concurrency: parseInt(e.target.value) || 10 })}
                  onBlur={() => handleBlur('execution_concurrency')}
                  disabled={isLoading}
                  min={1}
                  max={50}
                  className={`block w-full rounded-md shadow-sm sm:text-sm py-2 px-3 border ${
                    touched.execution_concurrency && errors.execution_concurrency
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                  }`}
                  aria-invalid={touched.execution_concurrency && !!errors.execution_concurrency}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Default: 10 (1-50)
                </p>
                {touched.execution_concurrency && errors.execution_concurrency && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {errors.execution_concurrency}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading && (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Upload & Create Batch
              </button>
            </div>
          </form>
      </div>
    </div>
  );
};
